import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    Box, AppBar, Toolbar, Typography, Stack, Chip, IconButton, Tooltip, useMediaQuery, CircularProgress,
} from "@mui/material";
import Compressor from "compressorjs";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import LogoutIcon from "@mui/icons-material/Logout";
import SettingsIcon from "@mui/icons-material/Settings";
import BarChartIcon from "@mui/icons-material/BarChart";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import livechatApi from "./livechatApi";
import { getAttachmentKind } from "./livechatDisplayUtils";
import { connectLivechatSocket, disconnectLivechatSocket } from "./livechatSocket";
import { LIVECHAT_PARTNER_BRAND } from "./livechatConstants";
import ChannelRail from "./ChannelRail";
import ConversationList from "./ConversationList";
import ChatPanel from "./ChatPanel";
import CustomerContextPanel from "./CustomerContextPanel";
import SetLabelDialog from "./SetLabelDialog";
import GenerateLinkDialog from "./GenerateLinkDialog";
import LiveChatSettingsDialog from "./LiveChatSettingsDialog";
import LiveChatDashboardDialog from "./LiveChatDashboardDialog";
import MitraContactsDialog from "./MitraContactsDialog";
import { playNotificationSound } from "./livechatSound";
import { PASTEL } from "./livechatTheme";
import Global from "../../customlib/Global";;

const LIVECHAT_MESSAGES_PAGE_SIZE = 50;

function compressImage(file) {
    return new Promise((resolve, reject) => {
        new Compressor(file, {
            quality: 0.8,
            maxWidth: 1600,
            maxHeight: 1600,
            success: resolve,
            error: reject,
        });
    });
}

// Halaman baru DEV-1049 — TIDAK memanggil Global.getAppProfile() sama sekali (App Profile
// tetap wajib dipilih saat login, tapi nilainya diabaikan total oleh modul ini). Auth ke
// lotus-be-express reuse Global.get().be_express_token yang sudah ada (lihat livechatApi.js).
export default function CrmLiveChat() {
    const isMobile = useMediaQuery("(max-width:900px)");
    const [brands, setBrands] = useState([]);
    const [activeBrand, setActiveBrand] = useState("all");
    const [filter, setFilter] = useState("active");
    const [search, setSearch] = useState("");
    const [sessions, setSessions] = useState([]);
    const [selectedSession, setSelectedSession] = useState(null);
    const [messages, setMessages] = useState([]);
    const [messagesHasMore, setMessagesHasMore] = useState(false);
    const [loadingOlderMessages, setLoadingOlderMessages] = useState(false);
    const nextMessagesPageRef = useRef(2);
    const [labelDialogOpen, setLabelDialogOpen] = useState(false);
    const [generatedLink, setGeneratedLink] = useState(null);
    const [unreadTotal, setUnreadTotal] = useState(0);
    const [channelUnread, setChannelUnread] = useState({});
    const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);
    const [dashboardDialogOpen, setDashboardDialogOpen] = useState(false);
    const [contactsDialogOpen, setContactsDialogOpen] = useState(false);
    const [contextPanelOpen, setContextPanelOpen] = useState(true);
    const isActiveRef = useRef(true);

    const socket = useMemo(() => connectLivechatSocket(), []);
    const [socketConnected, setSocketConnected] = useState(socket.connected);
    const [reconnecting, setReconnecting] = useState(false);

    useEffect(() => {
        // socket.connected sudah bisa true saat mount (koneksi dibuat sebelum effect ini jalan,
        // lihat connectLivechatSocket di atas) — makanya initial state useState juga baca
        // socket.connected langsung, bukan cuma andalkan event "connect" yang tidak akan fire lagi.
        const onConnect = () => { setSocketConnected(true); setReconnecting(false); };
        const onDisconnect = () => { setSocketConnected(false); setReconnecting(false); };
        socket.on("connect", onConnect);
        socket.on("disconnect", onDisconnect);
        socket.on("connect_error", onDisconnect);
        return () => {
            socket.off("connect", onConnect);
            socket.off("disconnect", onDisconnect);
            socket.off("connect_error", onDisconnect);
        };
    }, [socket]);

    // Socket.io sudah auto-reconnect sendiri di background, tapi kalau attempt-nya sedang jeda
    // (backoff) atau gagal terus, agent bisa lama menunggu tanpa kepastian — tombol ini
    // memicu percobaan connect() manual, langsung (skip sisa waktu backoff yang sedang berjalan).
    const handleReconnect = useCallback(() => {
        if (socket.connected || reconnecting) return;
        setReconnecting(true);
        socket.connect();
    }, [socket, reconnecting]);

    useEffect(() => {
        // Suara notifikasi dibuat lebih keras (loud) saat tab sedang tidak difokus agent —
        // visible saja tidak cukup (tab masih "visible" saat alt-tab ke app lain), makanya
        // digabung dengan window focus/blur. Mirror dari livechat-customer-fe/ChatScreen.jsx.
        const updateActivityState = () => {
            isActiveRef.current = document.visibilityState === "visible" && document.hasFocus();
        };
        updateActivityState();
        document.addEventListener("visibilitychange", updateActivityState);
        window.addEventListener("focus", updateActivityState);
        window.addEventListener("blur", updateActivityState);
        return () => {
            document.removeEventListener("visibilitychange", updateActivityState);
            window.removeEventListener("focus", updateActivityState);
            window.removeEventListener("blur", updateActivityState);
        };
    }, []);

    const loadInbox = useCallback(async () => {
        const params = {
            brand: activeBrand !== "all" ? activeBrand : undefined,
            status: filter === "closed" ? "closed" : filter === "active" ? "active" : undefined,
            unreadOnly: filter === "unread" ? "true" : undefined,
            search: search || undefined,
            limit: 50,
        };
        const { data } = await livechatApi.listInbox(params);
        setSessions(data.data || []);
        setUnreadTotal((data.data || []).filter((s) => s.unreadByAgent).length);
    }, [activeBrand, filter, search]);

    const loadBrands = useCallback(() => {
        livechatApi.listBrands().then(({ data }) => setBrands(data.data?.brands || []));
    }, []);

    // Unread count per-kanal untuk ChannelRail — independen dari filter/search/activeBrand
    // yang sedang aktif di ConversationList (rail harus tetap menampilkan total unread SEMUA
    // kanal, bukan cuma kanal yang lagi dipilih). limit:1 karena hanya `additional_data.total`
    // (bukan daftar sesi-nya) yang dipakai di sini.
    const loadChannelUnread = useCallback(async () => {
        const targets = [{ key: "all", brand: undefined }, ...brands.map((b) => ({ key: b.brand, brand: b.brand }))];
        const entries = await Promise.all(
            targets.map(({ key, brand }) =>
                livechatApi
                    .listInbox({ brand, unreadOnly: "true", limit: 1 })
                    .then(({ data }) => [key, data.additional_data?.total || 0])
                    .catch(() => [key, 0])
            )
        );
        setChannelUnread(Object.fromEntries(entries));
    }, [brands]);

    useEffect(() => {
        loadBrands();
    }, [loadBrands]);

    useEffect(() => {
        loadInbox();
    }, [loadInbox]);

    useEffect(() => {
        loadChannelUnread();
    }, [loadChannelUnread]);

    useEffect(() => {
        document.title = unreadTotal > 0 ? `(${unreadTotal}) Live Chat` : "Live Chat";
    }, [unreadTotal]);

    useEffect(() => {
        // Sesi baru (inbox:session:new) selalu berarti pesan pertama dari customer yang belum
        // dibaca (unreadByAgent selalu true saat sesi dibuat, lihat livechat.session.service.ts).
        // Untuk inbox:session:updated, backend TIDAK menyertakan `sender` di payload — tapi
        // unreadByAgent hanya bernilai true kalau update ini berasal dari pesan customer yang
        // belum ditandai dibaca (pesan agent sendiri tidak mengubah unreadByAgent, lihat
        // livechat.message.service.ts#createMessage), jadi flag ini sudah cukup untuk membedakan
        // "customer baru menulis" dari "echo pesan balasan agent sendiri" tanpa perlu field baru.
        const onNewSession = () => {
            loadInbox();
            loadChannelUnread();
            playNotificationSound(!isActiveRef.current);
        };
        const onSessionUpdated = (payload) => {
            loadInbox();
            loadChannelUnread();
            if (payload?.unreadByAgent) playNotificationSound(!isActiveRef.current);
        };
        socket.on("inbox:session:new", onNewSession);
        socket.on("inbox:session:updated", onSessionUpdated);
        return () => {
            socket.off("inbox:session:new", onNewSession);
            socket.off("inbox:session:updated", onSessionUpdated);
        };
    }, [socket, loadInbox, loadChannelUnread]);

    useEffect(() => {
        return () => disconnectLivechatSocket();
    }, []);

    // Dipakai baik oleh optimistic-update di handleSendMessage/handleSendAttachment maupun oleh
    // listener socket "conversation:message:new" — dedupe by _id supaya pesan yang sama tidak
    // dobel dirender kalau backend suatu saat juga meng-echo pesan milik sendiri lewat socket.
    const appendMessage = useCallback((msg) => {
        if (!msg) return;
        setMessages((prev) => (msg._id && prev.some((m) => m._id === msg._id)) ? prev : [...prev, msg]);
    }, []);

    const handleSelectSession = useCallback(async (session) => {
        setSelectedSession(session);
        const { data } = await livechatApi.getSessionDetail(session._id, { page: 1, limit: LIVECHAT_MESSAGES_PAGE_SIZE });
        setMessages(data.data?.messages || []);
        setMessagesHasMore(!!data.data?.hasMoreMessages);
        nextMessagesPageRef.current = 2;
        await livechatApi.markRead(session._id);
        loadInbox();
        loadChannelUnread();

        socket.emit("conversation:join", { brand: session.brand, phone: session.customerPhone });
    }, [socket, loadInbox, loadChannelUnread]);

    // Dipanggil ChatPanel saat pengguna scroll mentok ke atas daftar pesan — memuat halaman
    // pesan lebih LAMA (page 2, 3, ...) dan menaruhnya di AWAL array (bukan replace total seperti
    // handleSelectSession), dedupe by _id jaga-jaga kalau ada overlap antar halaman.
    const handleLoadOlderMessages = useCallback(async () => {
        if (!selectedSession || loadingOlderMessages || !messagesHasMore) return;
        setLoadingOlderMessages(true);
        try {
            const page = nextMessagesPageRef.current;
            const { data } = await livechatApi.getSessionDetail(selectedSession._id, { page, limit: LIVECHAT_MESSAGES_PAGE_SIZE });
            const older = data.data?.messages || [];
            setMessages((prev) => {
                const existingIds = new Set(prev.map((m) => m._id));
                const merged = older.filter((m) => !existingIds.has(m._id));
                return [...merged, ...prev];
            });
            setMessagesHasMore(!!data.data?.hasMoreMessages);
            nextMessagesPageRef.current = page + 1;
        } catch (error) {
            console.error("Error in CrmLiveChat.handleLoadOlderMessages:", error);
        } finally {
            setLoadingOlderMessages(false);
        }
    }, [selectedSession, loadingOlderMessages, messagesHasMore]);

    useEffect(() => {
        if (!selectedSession) return;

        const onNewMessage = (msg) => {
            if (msg.sessionId?.toString() !== selectedSession._id?.toString()) return;
            // Pesan milik agent sendiri sudah ditampilkan lewat jalur optimistic di
            // attemptSendMessage/attemptSendAttachment (bubble "sending" -> di-update jadi pesan asli
            // dari response REST). Backend ternyata tetap meng-echo event ini balik ke room
            // (termasuk ke socket pengirimnya), dan karena bubble optimistic masih ber-_id
            // sementara (tempId) saat echo ini tiba, dedupe by _id di appendMessage tidak akan
            // match -> jadi 2 bubble untuk pesan yang sama. Abaikan echo utk sender staff — "agent"
            // (channel customer) maupun "purchasing" (channel mitra-purchasing, DEV-1190/1193).
            if (msg.sender === "agent" || msg.sender === "purchasing") return;
            appendMessage(msg);
            // Agent sedang membuka percakapan ini saat pesan baru dari customer masuk -> tandai
            // dibaca langsung, jangan tunggu pilih ulang sesi (markRead sebelumnya cuma dipanggil
            // sekali saat handleSelectSession, jadi pesan yang masuk setelah itu tidak pernah
            // ditandai dibaca selama sesi masih terbuka).
            livechatApi.markRead(selectedSession._id).catch(() => {});
        };
        // Customer/mitra membuka kembali percakapan -> tandai pesan milik staff sebagai dibaca (✓✓),
        // tanpa perlu reload/refetch (lihat livechat.message.service.ts#markRead di backend).
        const onReadAck = ({ by, readAt }) => {
            if (by !== "customer") return;
            setMessages((prev) => prev.map((m) => (
                (m.sender === "agent" || m.sender === "purchasing") && !m.readByCustomerAt ? { ...m, readByCustomerAt: readAt } : m
            )));
        };

        socket.on("conversation:message:new", onNewMessage);
        socket.on("conversation:read:ack", onReadAck);
        return () => {
            socket.off("conversation:message:new", onNewMessage);
            socket.off("conversation:read:ack", onReadAck);
            if (selectedSession) {
                socket.emit("conversation:leave", { brand: selectedSession.brand, phone: selectedSession.customerPhone });
            }
        };
    }, [socket, selectedSession, appendMessage]);

    // Kirim pesan teks dibuat optimistic (bubble "sending" langsung tampil di daftar, di-update
    // jadi bubble asli dari response REST kalau sukses, atau "failed" + tombol retry kalau gagal)
    // — sebelumnya kegagalan di sini silent total: input sudah keburu dikosongkan di ChatPanel
    // dan tidak ada bubble apa pun yang menandai pesannya hilang.
    const attemptSendMessage = useCallback(async (tempId, sessionId, text) => {
        setMessages((prev) => prev.map((m) => (m._id === tempId ? { ...m, deliveryStatus: "sending" } : m)));
        try {
            const { data } = await livechatApi.sendMessage(sessionId, { text });
            const sent = data.data?.message || data.data;
            setMessages((prev) => prev.map((m) => (m._id === tempId ? { ...sent, deliveryStatus: "sent" } : m)));
        } catch (error) {
            console.error("Error in CrmLiveChat.attemptSendMessage:", error);
            setMessages((prev) => prev.map((m) => (m._id === tempId ? { ...m, deliveryStatus: "failed" } : m)));
        }
    }, []);

    const handleSendMessage = useCallback((text) => {
        if (!selectedSession) return;
        const sessionId = selectedSession._id;
        const tempId = `temp-${Date.now()}`;
        setMessages((prev) => [...prev, {
            _id: tempId, sender: "agent", text, deliveryStatus: "sending", created_at: new Date().toISOString(),
        }]);
        attemptSendMessage(tempId, sessionId, text);
    }, [selectedSession, attemptSendMessage]);

    // Kompresi dipindah ke sini (dari ChatPanel) supaya kegagalannya (bukan cuma upload/kirim)
    // ikut tertangkap oleh bubble optimistic yang sama, konsisten dengan pola
    // livechat-customer-fe/ChatScreen.jsx#handleSendAttachment (bubble "sending" dibuat DULU,
    // baru kompres+upload+kirim dibungkus try/catch — bukan kompres dulu baru bikin bubble).
    // Kompresi (compressorjs) hanya berlaku utk gambar — video/dokumen diunggah apa adanya.
    const attemptSendAttachment = useCallback(async (tempId, sessionId, file, caption, previewUrl) => {
        setMessages((prev) => prev.map((m) => (m._id === tempId ? { ...m, deliveryStatus: "sending" } : m)));
        try {
            const kind = getAttachmentKind(file.type);
            const uploadPayload = kind === "image" ? await compressImage(file) : file;
            const { data: uploadRes } = await livechatApi.uploadFile(uploadPayload);
            const url = uploadRes.data?.url;
            const attachmentType = uploadRes.data?.attachmentType;
            const attachmentName = uploadRes.data?.fileName;
            const { data } = await livechatApi.sendMessage(sessionId, {
                text: caption || undefined, attachmentUrl: url, attachmentType, attachmentName,
            });
            const sent = data.data?.message || data.data;
            setMessages((prev) => prev.map((m) => (m._id === tempId ? { ...sent, deliveryStatus: "sent" } : m)));
            URL.revokeObjectURL(previewUrl);
        } catch (error) {
            console.error("Error in CrmLiveChat.attemptSendAttachment:", error);
            setMessages((prev) => prev.map((m) => (m._id === tempId ? { ...m, deliveryStatus: "failed" } : m)));
        }
    }, []);

    const handleSendAttachment = useCallback((file, caption, previewUrl) => {
        if (!selectedSession) return;
        const sessionId = selectedSession._id;
        const tempId = `temp-${Date.now()}`;
        setMessages((prev) => [...prev, {
            _id: tempId, sender: "agent", text: caption || undefined,
            attachmentUrl: previewUrl, attachmentType: getAttachmentKind(file.type), attachmentName: file.name,
            deliveryStatus: "sending", created_at: new Date().toISOString(),
            // disimpan di bubble sendiri (bukan closure) supaya retry tetap bisa dipakai walau
            // komponen re-render/unmount di antara percobaan pertama & retry.
            _retryFile: file, _retryCaption: caption,
        }]);
        attemptSendAttachment(tempId, sessionId, file, caption, previewUrl);
    }, [selectedSession, attemptSendAttachment]);

    // Dipanggil dari tombol retry di bubble "Gagal terkirim" (ChatPanel). Sengaja pakai
    // selectedSession._id SAAT RETRY diklik (bukan menyimpan sessionId pesan gagal) — pesan
    // gagal cuma bisa terlihat & di-retry selama percakapan yang sama masih dibuka, karena
    // `messages` di-replace total tiap kali pindah sesi (lihat handleSelectSession).
    const handleRetry = useCallback((msg) => {
        if (msg.deliveryStatus !== "failed" || !selectedSession) return;
        if (msg._retryFile) {
            attemptSendAttachment(msg._id, selectedSession._id, msg._retryFile, msg._retryCaption, msg.attachmentUrl);
        } else {
            attemptSendMessage(msg._id, selectedSession._id, msg.text);
        }
    }, [selectedSession, attemptSendMessage, attemptSendAttachment]);

    // Setelah "Selesai", jangan diam di percakapan yang baru ditutup (masih memungkinkan agent
    // ngobrol lagi dari situ & auto-reopen sesi, membingungkan alurnya) — pindah otomatis ke
    // percakapan berikutnya di list yang sedang tampil, atau ke state kosong kalau memang tidak
    // ada percakapan lain (placeholder "Pilih percakapan..." di ChatPanel sudah menangani itu).
    const handleCloseSession = async () => {
        const closingId = selectedSession._id;
        await livechatApi.closeSession(closingId);

        const closedIndex = sessions.findIndex((s) => s._id === closingId);
        const remaining = sessions.filter((s) => s._id !== closingId);
        const nextSession = remaining[closedIndex] ?? remaining[closedIndex - 1] ?? null;

        if (nextSession) {
            handleSelectSession(nextSession);
        } else {
            setSelectedSession(null);
            setMessages([]);
        }
        loadInbox();
    };

    const handleGenerateLink = async () => {
        const { data } = await livechatApi.generateLink(selectedSession._id);
        setGeneratedLink(data.data?.url || "");
    };

    // DEV-1197 — dipanggil MitraContactsDialog setelah sesi berhasil dibuat/di-resume via
    // startSessionToMitra; reuse handleSelectSession supaya alur (load messages, markRead,
    // join room socket) sama persis dengan memilih sesi dari ConversationList.
    const handleMitraSessionStarted = useCallback((result) => {
        setContactsDialogOpen(false);
        if (result?.session) handleSelectSession(result.session);
        loadInbox();
        loadChannelUnread();
    }, [handleSelectSession, loadInbox, loadChannelUnread]);

    const canAccessMitraContacts = brands.some((b) => b.brand === LIVECHAT_PARTNER_BRAND);

    const handleLogout = () => {
        disconnectLivechatSocket();
        localStorage.removeItem("auth");
        localStorage.removeItem("selectedAppProfile");
        window.location.href = "/";
    };

    return (
        <Box sx={{ display: "flex", flexDirection: "column", height: "100vh" }}>
            <AppBar position="static" color="inherit" elevation={0} sx={{ borderBottom: `1px solid ${PASTEL.border}`, backgroundColor: PASTEL.surface }}>
                <Toolbar variant="dense" sx={{ minHeight: { xs: 56, sm: 64 }, px: { xs: 1.5, sm: 3 }, gap: { xs: 1, sm: 2.5 } }}>
                    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flexShrink: 0 }}>
                        <Box
                            sx={{
                                width: 36, height: 36, borderRadius: 2, flexShrink: 0,
                                display: { xs: "none", sm: "flex" }, alignItems: "center", justifyContent: "center",
                                backgroundColor: PASTEL.primarySoft,
                            }}
                        >
                            <ChatBubbleOutlineIcon sx={{ color: PASTEL.primary, fontSize: 20 }} />
                        </Box>
                        <Box>
                            <Stack direction="row" spacing={1} alignItems="center">
                                <Typography variant="subtitle1" fontWeight={700} lineHeight={1.2}>Live Chat</Typography>
                                {unreadTotal > 0 && (
                                    <Chip
                                        size="small"
                                        color="error"
                                        label={unreadTotal}
                                        sx={{ height: 18, fontSize: 10, fontWeight: 700 }}
                                    />
                                )}
                            </Stack>
                            <Typography variant="caption" color="text.secondary" sx={{ display: { xs: "none", sm: "block" } }}>
                                {sessions.length} percakapan
                            </Typography>
                        </Box>
                    </Stack>

                    <Box sx={{ flex: 1 }} />

                    <Tooltip title={
                        socketConnected ? "Terhubung ke server"
                            : reconnecting ? "Sedang mencoba menyambungkan ulang..."
                                : "Terputus dari server — klik untuk menyambungkan ulang"
                    }>
                        <Chip
                            size="small"
                            onClick={socketConnected ? undefined : handleReconnect}
                            icon={
                                reconnecting ? (
                                    <CircularProgress size={10} thickness={6} sx={{ color: "#c62828", ml: "8px !important" }} />
                                ) : socketConnected ? (
                                    <Box
                                        sx={{
                                            width: 8, height: 8, borderRadius: "50%", ml: "8px !important",
                                            backgroundColor: "#2e7d32",
                                        }}
                                    />
                                ) : (
                                    <RefreshRoundedIcon sx={{ fontSize: 14, ml: "4px !important", color: "#c62828" }} />
                                )
                            }
                            label={socketConnected ? "Terhubung" : reconnecting ? "Menyambungkan..." : "Terputus"}
                            sx={{
                                fontWeight: 600,
                                color: socketConnected ? "#2e7d32" : "#c62828",
                                backgroundColor: socketConnected ? "rgba(46,125,50,0.1)" : "rgba(198,40,40,0.1)",
                                cursor: socketConnected ? "default" : "pointer",
                                "& .MuiChip-icon": { mr: -0.5 },
                                "&:hover": socketConnected ? undefined : { backgroundColor: "rgba(198,40,40,0.18)" },
                            }}
                        />
                    </Tooltip>

                    {
                        canAccessMitraContacts && (
                            <Tooltip title="Daftar Kontak Mitra — mulai chat duluan">
                                <IconButton onClick={() => setContactsDialogOpen(true)} size="small" sx={{ color: "text.secondary" }}>
                                    <PeopleAltIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        )
                    }

                    {
                        Global.get().admin && (
                            <Tooltip title="Settings">
                                <IconButton onClick={() => setSettingsDialogOpen(true)} size="small" sx={{ color: "text.secondary" }}>
                                    <SettingsIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        )
                    }

                    {
                        // DEV-1213/1214 — dashboard performa Corporate, khusus Leader/SPV (dicek juga di backend).
                        Global.isTeleLeader() && (
                            <Tooltip title="Dashboard Performa Corporate">
                                <IconButton onClick={() => setDashboardDialogOpen(true)} size="small" sx={{ color: "text.secondary" }}>
                                    <BarChartIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        )
                    }

                    <Tooltip title="Logout">
                        <IconButton onClick={handleLogout} size="small" sx={{ color: "text.secondary" }}>
                            <LogoutIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Toolbar>
            </AppBar>

            <Box sx={{ flex: 1, display: "flex", overflow: "hidden" }}>
                {(!isMobile || !selectedSession) && (
                    <ChannelRail
                        brands={brands}
                        activeBrand={activeBrand}
                        onSelect={setActiveBrand}
                        unreadByBrand={channelUnread}
                    />
                )}

                {(!isMobile || !selectedSession) && (
                    <ConversationList
                        sessions={sessions}
                        selectedSessionId={selectedSession?._id}
                        onSelect={handleSelectSession}
                        filter={filter}
                        onFilterChange={setFilter}
                        search={search}
                        onSearchChange={setSearch}
                    />
                )}

                {(!isMobile || selectedSession) && (
                    <ChatPanel
                        session={selectedSession}
                        messages={messages}
                        hasMoreMessages={messagesHasMore}
                        loadingOlderMessages={loadingOlderMessages}
                        onLoadOlderMessages={handleLoadOlderMessages}
                        onSendMessage={handleSendMessage}
                        onSendAttachment={handleSendAttachment}
                        onRetryMessage={handleRetry}
                        onCloseSession={handleCloseSession}
                        onOpenLabelDialog={() => setLabelDialogOpen(true)}
                        isMobile={isMobile}
                        onBack={() => setSelectedSession(null)}
                        showContextPanelToggle={!isMobile}
                        contextPanelOpen={contextPanelOpen}
                        onToggleContextPanel={() => setContextPanelOpen((prev) => !prev)}
                    />
                )}

                {selectedSession && !isMobile && contextPanelOpen && (
                    <CustomerContextPanel
                        session={selectedSession}
                        onGenerateLink={handleGenerateLink}
                        onClose={() => setContextPanelOpen(false)}
                    />
                )}
            </Box>

            <SetLabelDialog
                open={labelDialogOpen}
                session={selectedSession}
                onClose={() => setLabelDialogOpen(false)}
                onSaved={(updated) => { setSelectedSession(updated); loadInbox(); }}
            />

            <GenerateLinkDialog
                open={generatedLink !== null}
                url={generatedLink}
                onClose={() => setGeneratedLink(null)}
            />

            <LiveChatSettingsDialog
                open={settingsDialogOpen}
                onClose={() => setSettingsDialogOpen(false)}
                onChanged={loadBrands}
            />

            <LiveChatDashboardDialog
                open={dashboardDialogOpen}
                onClose={() => setDashboardDialogOpen(false)}
            />

            <MitraContactsDialog
                open={contactsDialogOpen}
                onClose={() => setContactsDialogOpen(false)}
                onStarted={handleMitraSessionStarted}
            />
        </Box>
    );
}
